import React, { useMemo, useEffect, useState } from "react";
import { InputLabel, FormControl, MenuItem } from "@material-ui/core";
import type { FieldHookConfig, FieldAttributes } from "formik";
import { useField, Field, useFormikContext } from "formik";
import { Select } from "formik-material-ui";

import type { IPartnerSlim, IResponseWithLoading } from "App/Modules/Models";
import type { IPartnersRepository } from "App/Modules/Repositories";
import { extendWithLoadingAndFail } from "App/Modules/Services";

import { jsonEqual } from "../../Utils";

type OwnProps = {
    fullWidth: boolean;
    mmPartnersRepository: IPartnersRepository;
};

type PartnerSelectFieldProps<T extends Record<string, any>> = FieldHookConfig<T> & FieldAttributes<any> & OwnProps;

export const PartnerSelectField = <T extends Record<string, any>>(props: PartnerSelectFieldProps<T>) => {
    // eslint-disable-next-line @typescript-eslint/unbound-method -- Formik needs funcs to be unbound "function () {}" and not "() => {}" since "this" is injected by Formik
    const { setFieldValue } = useFormikContext();
    const [field] = useField(props);

    const fieldId = useMemo(() => `${field.name}-${Math.floor(Math.random() * 10000).toString()}`, [field.name]);
    const { fullWidth, mmPartnersRepository, ...otherProps } = props;

    const [loadingPartners, setLoadingPartners] = useState(true);
    const [partners, setPartners] = useState<IResponseWithLoading<IPartnerSlim[]>>();

    useEffect(() => {
        let sub;
        if (!partners) {
            sub = extendWithLoadingAndFail(mmPartnersRepository.getPartners()).subscribe(
                (response: IResponseWithLoading<IPartnerSlim[]>) => {
                    if (!response.isLoading && !jsonEqual(response, partners)) {
                        setPartners(response);
                        setLoadingPartners(false);
                    }
                },
                (error) => {
                    setPartners(error);
                    setLoadingPartners(false);
                },
            );
        }
        return () => {
            if (sub) {
                sub.unsubscribe();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (partners && partners.data.length === 1) {
            setFieldValue(field.name, partners.data[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partners]);

    return partners && partners.data.length === 1 ? (
        <Field type="hidden" {...field} {...otherProps} />
    ) : (
        <FormControl fullWidth={fullWidth}>
            <InputLabel htmlFor={fieldId}>{props.label}</InputLabel>
            <Field
                component={Select}
                inputProps={{
                    id: fieldId,
                }}
                {...field}
                {...otherProps}
            >
                {!loadingPartners
                    ? partners.data.map((p) => (
                          <MenuItem value={p.id} key={p.id}>
                              {p.name}
                          </MenuItem>
                      ))
                    : null}
            </Field>
        </FormControl>
    );
};
